import './PlaylistList.scss';
import React, { useRef } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import helpers from '../../js/helpers';
import { RESPONSE, ROUTES } from '../../js/_constants';
import FallbackCountries from '../fallbackCountries/FallbackCountries';
import ItemCard from '../ItemCard/ItemCard';
import { type Playlist } from '../../api/types/index';
import { useNavigate } from 'react-router';

type PlaylistListProps = {
    gridStyle: string;
    playlists: Playlist[];
    label: string;
    totalItems: number;
    tags?: React.ReactNode;
};

const PlaylistList = ({
    gridStyle,
    playlists,
    totalItems,
    tags
}: PlaylistListProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const isFallback = (playlist: Playlist) =>
        playlist.globalFallback ||
        (playlist.fallbackCountries && playlist.fallbackCountries.length > 0);

    const classes = classNames('b-playlistList', [gridStyle]);

    const ref = useRef(null);

    const renderFooter = (playlist: Playlist) => (
        <>
            <p className="valid-until">
                {t('playlist_last_edit')}: {helpers.humanDate(playlist.updatedAt) || t('no_data')}
            </p>
            {isFallback(playlist) && (
                <div className="fallback-row">
                    <span>Fallback:</span>
                    <FallbackCountries
                        isGlobalFallback={playlist.globalFallback}
                        fallbackCountries={playlist.fallbackCountries}
                    />
                </div>
            )}
        </>
    );

    const renderSubtitle = (playlist: Playlist) => (
        <span>
            {`${playlist.language || 'EN'} - ${helpers.humanDuration(
                playlist.duration
            )} Min`}
        </span>
    );

    return (
        <div ref={ref} className={classes}>
            <div className="playlist-result">
                <p>
                    <span className="result" data-testid='playlist-result'>{totalItems}</span>
                    {playlists.length > 1
                        ? t('playlist_meets')
                        : t('playlist_meet')}
                </p>
                {tags}
            </div>
            <ul className="playlists-wrapper">
                {playlists.map((playlist, i) => (
                    <ItemCard
                        key={playlist[RESPONSE.ID]}
                        isGridStyle={gridStyle === 'grid'}
                        thumbnail={playlist.thumbnail}
                        icon="media-playlist"
                        href={`${ROUTES.PLAYLISTS}/${helpers.getId(playlist)}`}
                        onClick={() =>
                            navigate(`${ROUTES.PLAYLISTS}/${helpers.getId(playlist)}`)
                        }
                        overlayTag={
                            isFallback(playlist) ? (
                                <span className="overlay-tag" data-testid='playlist-fallback-tag'>
                                    {t('playlist_fallback')}
                                </span>
                            ) : null
                        }
                        footer={renderFooter(playlist)}
                        title={playlist.title}
                        description={playlist.description}
                        subtitle={renderSubtitle(playlist)}
                        name={'playlist-' + (i + 1)}
                    />
                ))}
            </ul>
        </div>
    );
};

export default PlaylistList;
